'use client';

import { useState } from 'react';
import { format, getYear, isAfter, isBefore, parse, startOfMonth } from 'date-fns';
import { Calendar as CalendarIcon } from 'lucide-react';
import { Button } from '@/shared/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/shared/ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/ui/select';
import { cn } from '@/shared/utils/cn';

export interface MonthRange {
  from?: string; // yyyy-MM
  to?: string; // yyyy-MM
}

const MONTHS = Array.from({ length: 12 }, (_, i) => {
  const value = String(i + 1).padStart(2, '0');
  return {
    value,
    label: `${i + 1}월`,
    num: i + 1,
  };
});

interface MonthRangePickerProps {
  dateRange?: MonthRange;
  onDateRangeChange?: (dateRange: MonthRange | undefined) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  minDate?: Date;
  maxDate?: Date;
}

export const MonthRangePicker = ({
  dateRange,
  onDateRangeChange,
  placeholder = '월 범위를 선택하세요',
  disabled = false,
  className,
  minDate,
  maxDate = new Date(),
}: MonthRangePickerProps) => {
  const [open, setOpen] = useState(false);

  // 초기값 계산 (date-fns 활용)
  const now = new Date();
  const defaultDateStr = format(now, 'yyyy-MM');

  // 상태를 하나의 객체로 통합
  const [selectedDates, setSelectedDates] = useState<{
    fromYear: string;
    fromMonth: string;
    toYear: string;
    toMonth: string;
  }>(() => {
    const from = dateRange?.from || defaultDateStr;
    const to = dateRange?.to || defaultDateStr;
    const [fromYear, fromMonth] = from.split('-');
    const [toYear, toMonth] = to.split('-');
    return { fromYear, fromMonth, toYear, toMonth };
  });

  const fromDate = parse(`${selectedDates.fromYear}-${selectedDates.fromMonth}`, 'yyyy-MM', now);
  const toDate = parse(`${selectedDates.toYear}-${selectedDates.toMonth}`, 'yyyy-MM', now);

  const fromYearNum = getYear(fromDate);

  const minYear = minDate ? getYear(minDate) : null;
  const minDateStart = minDate ? startOfMonth(minDate) : null;
  const maxYear = getYear(maxDate);
  const maxDateStart = startOfMonth(maxDate);

  let displayText = placeholder;
  if (dateRange?.from) {
    displayText = dateRange.to ? `${dateRange.from} - ${dateRange.to}` : dateRange.from;
  }

  const startYear = minYear ?? maxYear - 10;
  const yearCount = maxYear - startYear + 1;
  const years = Array.from({ length: yearCount }, (_, i) => maxYear - i);

  const handleApply = () => {
    const from = `${selectedDates.fromYear}-${selectedDates.fromMonth}`;
    const to = `${selectedDates.toYear}-${selectedDates.toMonth}`;
    onDateRangeChange?.({ from, to });
    setOpen(false);
  };

  const updateDate = (type: 'fromYear' | 'fromMonth' | 'toYear' | 'toMonth', value: string) => {
    setSelectedDates((prev) => ({ ...prev, [type]: value }));
  };

  return (
    <Popover
      onOpenChange={setOpen}
      open={open}
    >
      <PopoverTrigger asChild>
        <Button
          className={cn(
            'w-full justify-start text-left font-normal',
            !dateRange && 'text-muted-foreground',
            className
          )}
          disabled={disabled}
          variant={'outline'}
        >
          <CalendarIcon className="mr-2 h-4 w-4" />
          {displayText}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-auto p-4"
      >
        <div className="space-y-4">
          <div className="space-y-2">
            <div className="text-sm font-medium">시작 월</div>
            <div className="flex gap-2">
              <Select
                onValueChange={(value) => updateDate('fromYear', value)}
                value={selectedDates.fromYear}
              >
                <SelectTrigger className="w-[120px]">
                  <SelectValue placeholder="년도" />
                </SelectTrigger>
                <SelectContent>
                  {years.map((year) => {
                    const isDisabled = minYear !== null && year < minYear;
                    return (
                      <SelectItem
                        disabled={isDisabled}
                        key={year}
                        value={String(year)}
                      >
                        {year}년
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
              <Select
                onValueChange={(value) => updateDate('fromMonth', value)}
                value={selectedDates.fromMonth}
              >
                <SelectTrigger className="w-[100px]">
                  <SelectValue placeholder="월" />
                </SelectTrigger>
                <SelectContent>
                  {MONTHS.map((month) => {
                    const monthDate = parse(
                      `${selectedDates.fromYear}-${month.value}`,
                      'yyyy-MM',
                      now
                    );
                    const isBeforeMinDate = minDateStart && isBefore(monthDate, minDateStart);
                    const isAfterToDate = isAfter(monthDate, toDate);

                    return (
                      <SelectItem
                        disabled={isBeforeMinDate || isAfterToDate}
                        key={month.value}
                        value={month.value}
                      >
                        {month.label}
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <div className="text-sm font-medium">종료 월</div>
            <div className="flex gap-2">
              <Select
                onValueChange={(value) => updateDate('toYear', value)}
                value={selectedDates.toYear}
              >
                <SelectTrigger className="w-[120px]">
                  <SelectValue placeholder="년도" />
                </SelectTrigger>
                <SelectContent>
                  {years.map((year) => {
                    const isAfterMaxDate = year > maxYear;
                    const isBeforeFromDate = year < fromYearNum;

                    return (
                      <SelectItem
                        disabled={isAfterMaxDate || isBeforeFromDate}
                        key={year}
                        value={String(year)}
                      >
                        {year}년
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
              <Select
                onValueChange={(value) => updateDate('toMonth', value)}
                value={selectedDates.toMonth}
              >
                <SelectTrigger className="w-[100px]">
                  <SelectValue placeholder="월" />
                </SelectTrigger>
                <SelectContent>
                  {MONTHS.map((month) => {
                    const monthDate = parse(
                      `${selectedDates.toYear}-${month.value}`,
                      'yyyy-MM',
                      now
                    );
                    const isAfterMaxDate = isAfter(monthDate, maxDateStart);
                    const isBeforeFromDate = isBefore(monthDate, fromDate);

                    return (
                      <SelectItem
                        disabled={isAfterMaxDate || isBeforeFromDate}
                        key={month.value}
                        value={month.value}
                      >
                        {month.label}
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>
          </div>

          <Button
            className="w-full"
            onClick={handleApply}
          >
            적용
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
};
